import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useMemo, useState } from "react";

const useStyles = makeStyles(() => ({
    sidebarContainer: {
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 1200,
    },
    sidebarCollapsed: {
        width: '94px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb',
    },
    sidebarExpanded: {
        width: '256px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        boxShadow: '4px 0 12px rgba(0, 0, 0, 0.1)',
    },
    logoSection: {
        height: '64px',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 16px',
    },
    logo: {
        width: '32px',
        height: '32px',
        backgroundColor: '#0d9488',
        borderRadius: '8px',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '18px',
    },
    logoText: {
        fontWeight: 600,
        color: '#111827',
    },
    toggleButton: {
        padding: '6px',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    navSection: {
        flex: 1,
        padding: '16px 12px',
    },
    navButton: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        transition: 'all 0.2s',
        textTransform: 'none',
        justifyContent: 'flex-start',
        minWidth: 0,
    },
    navButtonActive: {
        backgroundColor: '#f0fdfa',
        color: '#0f766e',
        '&:hover': {
            backgroundColor: '#f0fdfa',
        },
    },
    navButtonInactive: {
        color: '#374151',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    navIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
    },
    navIconActive: {
        color: '#0d9488',
    },
    navIconInactive: {
        color: '#6b7280',
    },
    navLabel: {
        flex: 1,
        textAlign: 'left',
        fontWeight: 500,
    },
    userSection: {
        borderTop: '1px solid #e5e7eb',
        padding: '16px',
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '14px',
    },
    userName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    userEmail: {
        fontSize: '12px',
        color: '#6b7280',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

type IconAtomProps = {
    icon: React.ElementType;
    className: string;
};

export const IconAtom: React.FC<IconAtomProps> = ({ icon: Icon, className }) => {
    return <Icon className={className} />;
};

type TextAtomProps = {
    text: string;
    className: string;
};

export const TextAtom: React.FC<TextAtomProps> = ({ text, className }) => {
    return <Typography className={className}>{text}</Typography>;
};

type LogoBoxAtomProps = {
    letter: string;
};

export const LogoBoxAtom: React.FC<LogoBoxAtomProps> = ({ letter }) => {
    const classes = useStyles();
    return (
        <Stack className={classes.logo} direction="row" alignItems="center" justifyContent="center">
            <Typography>{letter}</Typography>
        </Stack>
    );
};

type AvatarAtomProps = {
    initials: string;
};

export const AvatarAtom: React.FC<AvatarAtomProps> = ({ initials }) => {
    const classes = useStyles();
    return (
        <Stack className={classes.avatar} direction="row" alignItems="center" justifyContent="center">
            <Typography>{initials}</Typography>
        </Stack>
    );
};

type ToggleButtonAtomProps = {
    isExpanded: boolean;
    onClick: () => void;
};

export const ToggleButtonAtom: React.FC<ToggleButtonAtomProps> = ({ isExpanded, onClick }) => {
    const classes = useStyles();
    return (
        <IconButton onClick={onClick} className={classes.toggleButton}>
            {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
    );
};

type LogoMoleculeProps = {
    isExpanded: boolean;
};

export const LogoMolecule: React.FC<LogoMoleculeProps> = ({ isExpanded }) => {
    const classes = useStyles();
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <LogoBoxAtom letter="N" />
            {isExpanded && <TextAtom text="NaviCRM" className={classes.logoText} />}
        </Stack>
    );
};

type NavItemMoleculeProps = {
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    isExpanded: boolean;
    onClick: () => void;
};

export const NavItemMolecule: React.FC<NavItemMoleculeProps> = ({
    icon,
    label,
    isActive,
    isExpanded,
    onClick,
}) => {
    const classes = useStyles();
    const buttonClassName = `${classes.navButton} ${isActive ? classes.navButtonActive : classes.navButtonInactive}`;
    const iconClassName = `${classes.navIcon} ${isActive ? classes.navIconActive : classes.navIconInactive}`;

    return (
        <Button onClick={onClick} className={buttonClassName}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                <IconAtom icon={icon} className={iconClassName} />
                {isExpanded && <TextAtom text={label} className={classes.navLabel} />}
            </Stack>
        </Button>
    );
};

type UserProfileMoleculeProps = {
    isExpanded: boolean;
    name: string;
    email: string;
    initials: string;
};

export const UserProfileMolecule: React.FC<UserProfileMoleculeProps> = ({
    isExpanded,
    name,
    email,
    initials,
}) => {
    const classes = useStyles();
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <AvatarAtom initials={initials} />
            {isExpanded && (
                <Stack direction="column" spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                    <TextAtom text={name} className={classes.userName} />
                    <TextAtom text={email} className={classes.userEmail} />
                </Stack>
            )}
        </Stack>
    );
};

type NavItem = {
    id: string;
    icon: React.ElementType;
    label: string;
};

type NavigationOrganimProps = {
    items: NavItem[];
    activeId: string;
    isExpanded: boolean;
    onItemClick: (id: string) => void;
};

export const NavigationOrganism: React.FC<NavigationOrganimProps> = ({
    items,
    activeId,
    isExpanded,
    onItemClick,
}) => {
    return (
        <Stack direction="column" spacing={0.5}>
            {items.map((item) => {
                const isActive = item.id === activeId;

                return (
                    <NavItemMolecule
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        isActive={isActive}
                        isExpanded={isExpanded}
                        onClick={() => onItemClick(item.id)}
                    />
                );
            })}
        </Stack>
    );
};

type SidebarTemplateProps = {
    isExpanded: boolean;
    onToggle: () => void;
    activeNavId: string;
    onNavChange: (id: string) => void;
    navItems: NavItem[];
};

export const SidebarTemplate: React.FC<SidebarTemplateProps> = ({
    isExpanded,
    onToggle,
    activeNavId,
    onNavChange,
    navItems,
}) => {
    const classes = useStyles();

    return (
        <Box className={classes.sidebarContainer}>
            <Stack
                direction="column"
                className={isExpanded ? classes.sidebarExpanded : classes.sidebarCollapsed}
                sx={{ transition: 'width 0.3s ease-in-out' }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    className={classes.logoSection}
                >
                    <LogoMolecule isExpanded={isExpanded} />
                    <ToggleButtonAtom isExpanded={isExpanded} onClick={onToggle} />
                </Stack>

                <Box className={classes.navSection}>
                    <NavigationOrganism
                        items={navItems}
                        activeId={activeNavId}
                        isExpanded={isExpanded}
                        onItemClick={onNavChange}
                    />
                </Box>

                <Box className={classes.userSection}>
                    <UserProfileMolecule
                        isExpanded={isExpanded}
                        name="John Doe"
                        email="john@company.com"
                        initials="JD"
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export const SidebarPage: React.FC<{}> = () => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [activeNavId, setActiveNavId] = useState<string>('home');

    const navItems = useMemo<NavItem[]>(() => [
        { id: 'home', icon: HomeIcon, label: 'Home' },
        { id: 'new-deals', icon: DescriptionIcon, label: 'New Deals' },
        { id: 'company-updates', icon: NotificationsIcon, label: 'Company Updates' },
        { id: 'settings', icon: SettingsIcon, label: 'Settings' },
    ], []);

    const handleToggle = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleNavChange = useCallback((id: string) => {
        setActiveNavId(id);
    }, []);

    return (
        <SidebarTemplate
            isExpanded={isExpanded}
            onToggle={handleToggle}
            activeNavId={activeNavId}
            onNavChange={handleNavChange}
            navItems={navItems}
        />
    );
};

export default SidebarPage;